// 3rd-party modules
import moment from "moment-timezone";
import { isMobile } from "react-device-detect";
import { useEffect, useState } from "react";
import dayjs, { Dayjs } from 'dayjs';
import { TimePicker as AntTimePicker, TimePickerProps as AntTimePickerProps } from "antd";


// project modules
import Button from "../../shared/button";
import DataTable, { TableColumn } from "../../shared/list/dataTable";
import Loader from "../../shared/loader";
import OptionBar from "../../shared/optionBar";
import Radio, { RadioType } from "../../shared/inputs/radio";
import Input from "../../shared/inputs/input";
import Select from "../../shared/inputs/select";
import { apiCall } from "../../../helpers/apiHelper";
import { getNoiseDomains } from "../../../helpers/noiseDomains";
import { useCacheHook } from "../../../hooks/useCacheHook";
// import DashboardWidget from "../../global/dashboardWidget";
// import CustomChart from "../../shared/chart/chart";
import CustomChartModal from "../../shared/chart/chartModal";

// apis
import * as AccountKiboshDeviceApi from '../../../apis/accountKiboshDeviceApi';
import * as DashApi from '../../../apis/dashApi';

// models
import Checkbox from "../../shared/inputs/checkbox";
import { Account } from "../../../models/account";
import { AccountDeviceGroup } from '../../../models/accountDeviceGroup';
import { AccountKiboshDevice } from "../../../models/accountKiboshDevice";
import { AdminFilterSet } from "../../../models/adminFilterSet";
import { DataSourceRequest, FilterDescriptor, PaginationDescriptor, SortDescriptor } from "../../../models/dataSourceRequest";
import { AccountDevice, AccountDeviceActivityFrequencyLog, AccountDeviceActivityLog } from "../../../models/accountDevice";
// import { message } from "antd";

// defines
import { CONSTANTS } from "../../../helpers/defines";
import Timeline from "../../shared/timeLine";

type Props = {
  account?: Account;
  deviceReference?: string;
  deviceGroupReference?: string;
  hasUpperLevelControl?: boolean;
  kiboshDeviceReference?: string;
  isDomainBased?: boolean;
  refresh?: boolean;
  isVpn?: boolean;
  isStandalone?: boolean;
  onCancel?: () => void;
  onSave?: (account: Account) => void;
};

const TOP_SITES_COUNT = 10;
const TIME_SLICE_MINUTES = 20;

export default function AccountDeviceActivityLogs({ account, deviceReference, deviceGroupReference, hasUpperLevelControl = false, kiboshDeviceReference, refresh, isVpn = false, isStandalone = false, isDomainBased = false, onCancel, onSave }: Props) {
  const expandedRawTableColumns: TableColumn<any>[] = [
    {
      title: 'Query Time',
      // dataIndex: 'queryTime',
      // key: 'queryTime',
      filterable: false,
      // width: 100,
      dataType: 'datetime',
      render: (text: any) => <text>{moment(text).format(CONSTANTS.DEFAULT_TIME_FORMAT)}</text>
    }
  ];
  const expandedTableColumns: TableColumn<any>[] = [
    {
      title: 'Site',
      dataIndex: 'domain',
      key: 'domain',
      filterable: !isMobile,
      render: (text: any) => <text data-user-select="text">{text}</text>
    },
    {
      title: 'Time',
      dataIndex: 'time',
      key: 'time',
      filterable: false,
      // width: 100,
      dataType: 'datetime',
      render: (text: any) => <text>{moment(text).format(CONSTANTS.DEFAULT_TIME_FORMAT)}</text>
    }
  ];
  const [accountKiboshDevices, setAccountKiboshDevices] = useState<AccountKiboshDevice[]>([]);
  const [accountDevices, setAccountDevices] = useState<AccountDevice[]|AccountKiboshDevice[]>([]);
  const [activityDays, setActivityDays] = useState<string[]>([]);
  const [currentAccountKiboshDeviceReference, setCurrentAccountKiboshDeviceReference] = useState<string>("");
  const [currentAccountDeviceReference, setCurrentAccountDeviceReference] = useState<string>("");
  const [currentActivityDay, setCurrentActivityDay] = useState<string>("");
  // const [isActivityLogsEnabled, setIsActivityLogsEnabled] = useState(false);
  const [isCurrentDomainBased, setIsCurrentDomainBased] = useState(true);
  const [loading, setLoading] = useState(false);
  const [loadingAccountKiboshDevices, setLoadingAccountKiboshDevices] = useState(false);
  // const [loadingIsActivityLogsEnabled, setLoadingIsActivityLogsEnabled] = useState(false);
  const [loadingAccountDevices, setLoadingAccountDevices] = useState(false);
  const [loadingActivityDays, setLoadingActivityDays] = useState(false);
  const [loadingAdminFilterSet, setLoadingAdminFilterSet] = useState(false);
  const [showChartModal, setShowChartModal] = useState(false);
  const [showTimelineChartModal, setShowTimelineChartModal] = useState(false);

  const [reload, setReload] = useState<boolean>(false);
  const [forceReload, setForceReload] = useState<boolean>(false);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [radioMode, setRadioMode] = useState<string>("top_sites");
  const [chartDatasource, setChartDatasource] = useState<any[]>([]);
  const [timeChartData, setTimeChartData] = useState<any>(null);
  
  const [logSearchText, setLogSearchText] = useState<string>("");
  const [timeType, setTimeType]           = useState<string>("all_day")
  const [fromTime, setFromTime]           = useState<Dayjs>( dayjs( "2025-09-01 12:00 am" ) );
  const [toTime, setToTime]               = useState<Dayjs>( dayjs( "2025-09-01 11:45 pm" )  );

  const [cachedData, setCachedData] = useCacheHook();

  let abortController = new AbortController();

  const columns: TableColumn<any>[] = [
    {
      title: radioMode === 'raw' ? "Domain" : "Site",
      dataIndex: 'domain',
      key: 'domain',
      filterable: !isMobile,
      render: (text: any) => <text data-wrap="overflow-wrap" data-user-select={radioMode !== 'top_sites' ? "text" : undefined}>{text}</text>
    },
    {
      title: 'Time',
      dataIndex: 'queryTime',
      key: 'queryTime',
      filterable: !isMobile,
      width: 140,
      dataType: 'datetime',
      render: (text: any) => <text>{moment(text).format(CONSTANTS.DEFAULT_TIME_FORMAT)}</text>
    }
  ];

  const groupedColumns: TableColumn<any>[] = [
    {
      title: radioMode === 'raw' ? "Domain" : "Site",
      dataIndex: 'domain',
      key: 'domain',
      filterable: !isMobile,
      render: (text: any) => <text data-wrap="overflow-wrap" data-user-select={radioMode !== 'top_sites' ? "text" : undefined}>{text}</text>
    },
    {
      title: isMobile ? '#' : 'Count',
      dataIndex: 'count',
      key: 'count',
      filterable: !isMobile,
      width: isMobile ? 45 : 100
    },
    {
      title: isMobile ? '%' : 'Percent',
      dataIndex: 'percent',
      key: 'percent',
      filterable: !isMobile,
      width: isMobile ? 45 : 100,
      render: (text: any) => <>{`${text}%`}</>
    }
  ];

  useEffect(() => {
    if (reload) {
      setReload(false)
    };
  }, [reload]);

  useEffect(() => {
    if (forceReload) {
      setForceReload(false);
      setReload(true);
    }
  }, [forceReload]);

  useEffect(() => {
    if (refresh) {
      if (!kiboshDeviceReference) {
        getAccountKiboshDevicesAsync();
      } else if (!deviceReference) {
        getAccountDevicesAsync();
      }
      else {
        // getDeviceActivityEnableStateAsync();
        getDeviceActivityDaysAsync();
      }
    }
  }, [refresh]);

  useEffect(() => {
    if (activityDays.length > 0)
      onLogDaysChange(activityDays[0]);
  }, [activityDays])

  useEffect(() => {
    setForceReload(true);
    
    setIsCurrentDomainBased( radioMode == "top_sites" )

  }, [radioMode])

  const getAccountDevicesAsync = async (routerRef?: string) => {
    setAccountDevices(await getAccountDevices(routerRef, abortController.signal));
  }

  const getAccountDevices = async (routerRef?: string, abortSignal?: AbortSignal) => {

    setLoadingAccountDevices(true);
    if( kiboshDeviceReference!.startsWith("d-") && deviceGroupReference) {


      const gResponse = await apiCall(DashApi.getDeviceGroups(kiboshDeviceReference!, false, abortController.signal));
      const gData = gResponse.success ? AccountDeviceGroup.toArrayOfClass(gResponse.data?.value || []).sort((a: any, b: any) => a.id - b.id) : []


      let idsInGroups:any = {} 
      let idsInCurrentGroup:any = {}
      gData.forEach(group => {
        if(group.clientIds != null) {
          group.clientIds!.forEach( cl_id => {
            idsInGroups[cl_id] = true ;
            if(group.id == deviceGroupReference) {
              idsInCurrentGroup[cl_id] = true ;
            }
          });
        }
      });



      const kdResponse = await apiCall(AccountKiboshDeviceApi.getAccountKiboshDevices(account?.accountId!, {}, abortSignal));
      const kdData = kdResponse.success ? AccountKiboshDevice.toArrayOfClass(kdResponse.data?.value || []) : [];

      let groupDevices = kdData.filter( (d:any) => { return d.kiboshDeviceTypeCode?.toLowerCase() == "vpn" && d.hasActiveSubscription! && (idsInCurrentGroup[ d.kiboshDeviceReference! ] == true); } )

      setLoadingAccountDevices(false);


      return groupDevices.sort((a: any, b: any) => a.kiboshDeviceReference!.toLowerCase().localeCompare( b.kiboshDeviceReference!.toLowerCase()) );


    } else {
      const response = await apiCall(DashApi.getClientDevices(kiboshDeviceReference || currentAccountKiboshDeviceReference || routerRef!, abortSignal));
      setLoadingAccountDevices(false);

      let data = response.success ? AccountDevice.toArrayOfClass(response.data?.value || []) : [];

      if (deviceGroupReference) {
        data = data.filter(x => x.groupId === deviceGroupReference);
      } else {
        data = data.filter((x: any) => { return moment().add(-7, "days") < moment(x.updatedAt) || x.isVpn ; } )
      }


      return deviceGroupReference ? data : data.map((x: any) => {
        return {
          ...x,
          sorter: x.groupId ? Number(x.groupId) : Infinity
        }
      }).sort((a: any, b: any) => a.sorter - b.sorter);

    }
  };

  const getAccountKiboshDevicesAsync = async () => {
    setAccountKiboshDevices(await getAccountKiboshDevices({}, abortController.signal));
  }

  const getAccountKiboshDevices = async (request?: DataSourceRequest, abortSignal?: AbortSignal) => {
    setLoadingAccountKiboshDevices(true);
    const response = await apiCall(AccountKiboshDeviceApi.getAccountKiboshDevices(account?.accountId!, request, abortSignal));
    setLoadingAccountKiboshDevices(false);

    return response.success ? AccountKiboshDevice.toArrayOfClass(response.data?.value || []) : [];
  };

  // const getDeviceActivityEnableStateAsync = async (deviceRef?: string) => {
  //   const data = await getDeviceActivityLogEnableState(deviceRef, abortController.signal);
  //   setIsActivityLogsEnabled(data);
  //   if (data) getDeviceActivityDaysAsync(deviceRef);
  // }

  // const getDeviceActivityLogEnableState = async (deviceRef?: string, abortSignal?: AbortSignal) => {
  //   setLoadingIsActivityLogsEnabled(true);
  //   const response = await apiCall(DashApi.getDeviceActivityLogEnableState(kiboshDeviceReference || currentAccountKiboshDeviceReference, deviceRef || deviceReference || currentAccountDeviceReference, abortSignal));
  //   setLoadingIsActivityLogsEnabled(false);

  //   return response.success ? response.data?.value : false;
  // };

  const getDeviceActivityDaysAsync = async (deviceRef?: string) => {
    setActivityDays(await getDeviceActivityDays(deviceRef, abortController.signal));
  }

  const getDeviceActivityDays = async (deviceRef?: string, abortSignal?: AbortSignal) => {
    setLoadingActivityDays(true);
    const response = await apiCall(DashApi.getDeviceActivityDays(kiboshDeviceReference || currentAccountKiboshDeviceReference, deviceRef || deviceReference || currentAccountDeviceReference, isVpn, isStandalone, abortSignal));
    setLoadingActivityDays(false);

    return response.success ? response.data?.value || [] : [];
  };

  const getKiboshDeviceFilterSets = async (abortSignal?: AbortSignal) => {
    if (cachedData['KiboshDeviceFilterSets'] != null && cachedData['KiboshDeviceFilterSets'].length > 0) return cachedData['KiboshDeviceFilterSets'];

    setLoadingAdminFilterSet(true);
    const response = await apiCall(DashApi.getKiboshDeviceFilterSets(kiboshDeviceReference || currentAccountKiboshDeviceReference, abortSignal));
    setCachedData('KiboshDeviceFilterSets', AdminFilterSet.toArrayOfClass(response.data?.value || []).sort((a: AdminFilterSet, b: AdminFilterSet) => (a.displayOrder || 0) - (b.displayOrder || 0)));
    setLoadingAdminFilterSet(false);

    return response.success ? AdminFilterSet.toArrayOfClass(response.data?.value || []) : [];
  };

  const getPage = async (conditionList?: FilterDescriptor[], sortList?: SortDescriptor[], pagination?: PaginationDescriptor, abortSignal?: AbortSignal) => {
    if (!currentActivityDay)
      return [];

    //if( (kiboshDeviceReference || currentAccountKiboshDeviceReference) == "d-11171-1" ) {
    //  alert("beginning query")
    //}

    setLoading(true);
    let response = null;
    if (isCurrentDomainBased) {
      response = await apiCall(DashApi.getDeviceActivitiesDomainBased(kiboshDeviceReference || currentAccountKiboshDeviceReference, deviceReference || currentAccountDeviceReference, radioMode, isVpn, isStandalone, currentActivityDay, abortSignal));
    } else {
      response = await apiCall(DashApi.getDeviceActivities(kiboshDeviceReference || currentAccountKiboshDeviceReference, deviceReference || currentAccountDeviceReference, radioMode, isVpn, isStandalone, currentActivityDay, abortSignal));
    }
    setTotalRecords(response.success ? response.extra?.totalCount || 0 : 0);
    setLoading(false);

    //if( (kiboshDeviceReference || currentAccountKiboshDeviceReference) == "d-11171-1" ) {
    //  alert("ending query")
    //}


    let data = response.success ? response.data?.value : [];

    if (isCurrentDomainBased && radioMode !== "raw") {
      data = data.map((x: any) => {
        return {
          ...x,
          queryData: x.query_times?.map((t: any, index: any) => {
            return {
              time: t,
              domain: x.query_domains[index] || x.site || x.top_domain
            }
          })
        }
      })
    }

    if (radioMode === "top_sites") {
      const adminFilterSets: AdminFilterSet[] = await getKiboshDeviceFilterSets(abortSignal);
      data = data.map((x: any) => {
        return {
          ...x,
          domain: adminFilterSets.find(filter => filter.id === x.site)?.name
        }
      }).map((x: any) => {
        return {
          ...x,
          domain: x.domain?.replace("Safe Mode", "").replace("-BLOCK", "")
        }
      })
    } else if (radioMode === "logs") {
      let nd = getNoiseDomains();
      data = data.map((x: any) => {
        return {
          ...x,
          domain: x.top_domain
        }}).filter( (x:any) => { return !(nd[x.domain]); } ) 
    }

    //if( (kiboshDeviceReference || currentAccountKiboshDeviceReference) == "d-11171-1" ) {
    //  alert("end processing: " + data.length + " elements")
    //}


    // if (radioMode === "logs") {
    //   data = data.sort((a: any, b: any) => b.percent - a.percent).slice(0, TOP_SITES_COUNT)
    // }

    if (isCurrentDomainBased
      // || radioMode === "logs"
    ) {
      const returnData = response.success ? AccountDeviceActivityFrequencyLog.toArrayOfClass(data) : [];
      setChartDatasource(returnData.sort((a: any, b: any) => b.percent - a.percent).slice(0, TOP_SITES_COUNT));
      return returnData;
    } else {
      const returnData = response.success ? AccountDeviceActivityLog.toArrayOfClass(data) : [];
      return filterData(returnData);
    }
  };









  const exportRawLogs = async (abortSignal?: AbortSignal) => {
    if (!currentActivityDay)
      return [];

    let response = await apiCall(DashApi.getDeviceActivities(kiboshDeviceReference || currentAccountKiboshDeviceReference, deviceReference || currentAccountDeviceReference, "raw", isVpn, isStandalone, currentActivityDay, abortSignal));
    let data = response.success ? response.data?.value : [];
    let csvElements = data.map( (x: any) => { return [ x.query_time + "," + x.domain ] } )
    let csv = ([ "query_time,domain" ] ).concat(csvElements).join("\n")

    let csvBlob = new Blob([csv], {type: 'text/csv'});
    let csvURL = window.URL.createObjectURL(csvBlob);
    let tempLink = document.createElement('a');
    tempLink.href = csvURL;
    tempLink.setAttribute('download', 'raw_logs.csv');
    tempLink.click();


  }











  const filterData = (tableData:any) => {

    let td = tableData;
    if( (!isCurrentDomainBased) ) {


      if(logSearchText != "") {
       td = td.filter( (x:any) => { return x.domain.toLowerCase().includes(logSearchText.toLowerCase()) ; } )
      }

      if(timeType != "all_day") {
        let from = 0;
        let to = 24*60;
	if(timeType == "after" || timeType == "between") {
          from = 60*parseInt(fromTime.format("H")) + parseInt(fromTime.format("m"))
        }
        if(timeType == "before" || timeType == "between") {
          to = 60*parseInt(toTime.format("H")) + parseInt(toTime.format("m"))
        }
        td = td.filter( (x:any) => {
          let testTime = dayjs(x.queryTime)
          let test = 60*parseInt(testTime.format("H")) + parseInt(testTime.format("m"))
          return test >= from && test <= to
        } )
      }

   }
       
    return td
     
  };


  const onAccountKiboshDeviceChange = (kiboshDeviceReference: any) => {
    setCurrentAccountKiboshDeviceReference(kiboshDeviceReference);
    getAccountDevicesAsync(kiboshDeviceReference);
  }

  const onAccountDeviceChange = (deviceReference: any) => {
    setCurrentAccountDeviceReference(deviceReference);
    // getDeviceActivityEnableStateAsync(deviceReference);
    getDeviceActivityDaysAsync(deviceReference);
    // if (isActivityLogsEnabled) {
    //   getDeviceActivityDaysAsync(deviceReference);
    // }
  }

  const onLogDaysChange = (logDay: any) => {
    setCurrentActivityDay(logDay);
    setForceReload(true);
  }

  // const onActivityLogsEnabledCheckboxChange = async (event: any) => {
  //   setLoadingIsActivityLogsEnabled(true);
  //   const response = await apiCall(DashApi.updateDeviceActivityLogEnableState(kiboshDeviceReference || currentAccountKiboshDeviceReference, deviceReference || currentAccountDeviceReference, event.target?.checked, abortController.signal));

  //   if (response.success) {
  //     message.success("Activity Logs enabled state changed successfully.");
  //     getDeviceActivityEnableStateAsync();
  //   } else {
  //     message.error(response.error?.value);
  //     setLoadingIsActivityLogsEnabled(false);
  //   }
  // }

  const onDomainBasedCheckboxChange = async (event: any) => {
    setIsCurrentDomainBased(!isCurrentDomainBased);
    setForceReload(true);
  }

  // const onTimeLineChartCloseClick = () => {
  //   setShowTimelineChartModal(false);
  //   setTimeChartData(null);
  // }

  const getBlockableOption = (blockableItem:any) => {
    if (blockableItem instanceof AccountKiboshDevice) {
      return { text: "" + blockableItem.kiboshDeviceReference!, value: "" + blockableItem.kiboshDeviceReference! };

    } else {

      let isProfile = blockableItem["client_ids"] != null
      let isVpn = ( blockableItem["is_vpn"] == true || blockableItem["isVpn"] == true)
      let bv =  blockableItem.id! 
      let bt  =  blockableItem.name!
      bt = bt == null ? "" + blockableItem.id! : bt
      bt = isProfile ? bt + " (Profile)" : bt
      bt = isVpn ? bt  + " (VPN)" : bt
      bt = (!isProfile) && (!isVpn) && blockableItem.ip ? bt + " (" + blockableItem.ip.replace("disconnected", "offline") + ")" : bt
      return { text: bt, value: "" + bv }
    };
  }

  return (
    <>
      {loading && <Loader />}
      <view>
        <group data-space="15" data-border="" data-gap="5">
          <group data-width="auto" data-gap="15">
            {(!kiboshDeviceReference || !deviceReference) && (
              <>
                {!kiboshDeviceReference && (
                  <>
                    <Select
                      autoComplete=""
                      label="Kibosh Device"
                      labelPosition="left"
                      dataLength="320"
                      onChange={onAccountKiboshDeviceChange}
                      loading={loadingAccountKiboshDevices}
                      allowSearch={true}
                      options={
                        accountKiboshDevices?.map((item) => {
                          return {
                            text: `${item.kiboshDeviceName!} (${item.kiboshDeviceReference!})`,
                            value: item.kiboshDeviceReference!,
                          };
                        }) || []
                      }
                      button={
                        <Button
                          material
                          icon="refresh"
                          onClick={getAccountKiboshDevicesAsync}
                        />
                      }
                    />
                  </>
                )}
                {!deviceReference && (
                  <>
                    <Select
                      autoComplete=""
                      disabled={
                        loadingAccountDevices ||
                        !(
                          kiboshDeviceReference ||
                          currentAccountKiboshDeviceReference
                        )
                      }
                      label="Device"
                      placeholder="Select Device"
                      labelPosition="left"
                      dataLength="425"
                      dataMinLength="425"
                      onChange={onAccountDeviceChange}
                      loading={loadingAccountDevices}
                      allowSearch={true}
                      options={
                        accountDevices?.map((item) => { return getBlockableOption(item) }) || []
                      }
                      button={
                        <Button
                          material
                          icon="refresh"
                          onClick={getAccountDevicesAsync}
                        />
                      }
                    />
                  </>
                )}
              </>
            )}
            {/*!deviceGroupReference &&
              <>
                {loadingIsActivityLogsEnabled && <Loader />}
                <Checkbox
                  checked={isActivityLogsEnabled}
                  disabled={
                    loadingIsActivityLogsEnabled ||
                    !(deviceReference || currentAccountDeviceReference) || hasUpperLevelControl
                  }
                  label="Activity Logs Enabled"
                  minimal
                  dataLength="auto"
                  onChange={onActivityLogsEnabledCheckboxChange}
                />
              </>
            */}
          </group>
          {
          // (isActivityLogsEnabled || !!deviceGroupReference) &&
          (
            <group data-width="auto" data-gap="15" data-align="center">
              {/* <separator vertical="" data-height="20" data-adaptive="desktop"></separator> */}
              <Select
                value={currentActivityDay} //?
                disabled={!deviceReference && !currentAccountDeviceReference}
                autoComplete=""
                label="Display Logs For"
                placeholder="Select Day"
                labelPosition="left"
                dataLength="425"
                dataMinLength="425"
                onChange={onLogDaysChange}
                loading={loadingActivityDays}
                allowSearch={true}
                options={activityDays || []}
                button={
                  <Button
                    disabled={
                      !deviceReference && !currentAccountDeviceReference
                    }
                    material
                    icon="refresh"
                    onClick={() => getDeviceActivityDaysAsync()}
                  />
                }
              />
            </group>
          )}
        </group>

        {
        // (isActivityLogsEnabled || !!deviceGroupReference) &&
        (
          <>
            <view className="table_cont" data-background="none">
              <group
                data-space="15"
                data-gap="20"
                data-border=""
                data-background="highlight"
              >
                <group>
                  <OptionBar autosize animate>
                    <Radio
                      radioType={RadioType.Button}
                      radioValue="top_sites"
                      label="Top Sites"
                      dataLength="auto"
                      value={radioMode}
                      onClick={(e) => setRadioMode("top_sites")}
                    />
                    {   !((""+(kiboshDeviceReference || currentAccountKiboshDeviceReference)).startsWith("d-")) &&
                    <Radio
                      radioType={RadioType.Button}
                      radioValue="logs"
                      label="Logs"
                      dataLength="auto"
                      value={radioMode}
                      onClick={(e) => setRadioMode("logs")}
                    />
                    }
                    <Radio
                      radioType={RadioType.Button}
                      radioValue="raw"
                      label="Raw Logs"
                      dataLength="auto"
                      value={radioMode}
                      onClick={(e) => setRadioMode("raw")}
                    />
                  </OptionBar>
                    <Button
                      data-position="right"
                      style={{ marginRight:"3px" }}
                      material
                      text="Export Raw Logs"
                      primary
                      onClick={() => exportRawLogs() }
                    />

                  {
                    !!chartDatasource.length && radioMode === "top_sites" && isCurrentDomainBased &&
                    <Button
                      material
                      icon="leaderboard"
                      text="Chart"
                      primary
                      onClick={() => setShowChartModal(true)}
                    />
                  }
                </group>
                <Checkbox
                  checked={isCurrentDomainBased}
                  label={isCurrentDomainBased ? "Single view domain" : "Group view domain"}
                  minimal
                  dataLength="auto"
                  onChange={onDomainBasedCheckboxChange}
                />
                <Button
                  data-position="right"
                  material
                  icon="refresh"
                  text="Refresh"
                  outline
                  onClick={() => setForceReload(true)}
                />
                { !isCurrentDomainBased &&
                  <>
                  <group>
                    <Input
                      type="text"
                      name="log_search"
                      value={ logSearchText }
                      onChange={ (e:any) => { setLogSearchText(e.target.value); }}
                      onKeyPress={ (e:any) => {if(e.key == 'Enter'){setForceReload(true);}}}
                      placeholder="Search"
                      style={{ minWidth: "335px", width: "335px", paddingLeft: "5px" }}
                    />
                    <Button
                      material
                      icon="search"
                      text=""
                      outline
                      onClick={() => setForceReload(true)}
                    />
                  </group>
                  <group data-gap="3">
                    <Select
                      value={timeType} //?
                      autoComplete=""
                      labelPosition="left"
                      onChange={ (tt:any) => { setTimeType(tt); setReload(true); } }
                      allowSearch={false}
                      options={[ {value:"all_day", text:"All Day"}, {value:"after", text:"After"}, {value:"before", text:"Before"},{value:"between", text:"Between"} ]}
                    />

                    { (timeType == "after" ||  timeType == "between") &&
                      <AntTimePicker
                        name="fromTime"
                        //label="From Time"
                        needConfirm={false}
                        minuteStep={15}
                        format="hh:mm a"
                        value={ fromTime }
                        onChange={ (t) => { setFromTime(t) ; setReload(true); } }
                      />
                    }
                    { timeType == "between" && <text style={{ paddingTop:"5px"  }} data-weight="700"> - </text> }
                    { (timeType == "before" ||  timeType == "between") &&

                      <AntTimePicker
                        name="toTime"
                        //label="To Time"
                        needConfirm={false}
                        minuteStep={15}
                        format="hh:mm a"
                        value={ toTime }
                        onChange={ (t) => { setToTime(t) ; setReload(true); } }
                      />
                    }
                  </group>
                  </>

                }

              </group>
              {/* {radioMode === "logs" && (
                <group
                  data-type="grid"
                  data-gap="10"
                  data-space="15"
                  data-grid-template="300"
                  data-background="highlight"
                  data-border=""
                >
                  <DashboardWidget
                    title="Top Sites"
                    height=""
                    childSpace="0"
                    // headerButtonsContainer={
                    //   <>
                    //     <Tooltip title="Maximize">
                    //       <div
                    //         className="button micro"
                    //         data-position="right"
                    //         onClick={() => setOpenDownloadChartInModal(true)}
                    //       >
                    //         <icon>fullscreen</icon>
                    //       </div>
                    //     </Tooltip>
                    //   </>
                    // }
                  >
                    <CustomChart
                      title="Total"
                      options={{
                        chart: {
                          height: 200,
                          type: 'pie',
                        },
                        plotOptions: {
                          pie: {
                            customScale: 0.8,
                            size: 200
                          }
                        },
                        grid: {
                          row: {
                            colors: ['#f3f3f3', 'transparent'], // takes an array which will be repeated on columns
                            opacity: 0.5
                          },
                        },
                        tooltip: {
                          y: {
                            formatter: function (val: any) {
                              return `${Number(val).toFixed(2)} MB`;
                            }
                          }
                        },
                        labels: dataSource.map(x => x.domain),
                        legend: {
                          position: 'bottom'
                        }
                      }}
                      series={dataSource.map(x => x.percent)}
                      type="pie"
                      width="100%"
                      // openInModal={openTotalChartInModal}
                      // onClose={() => setOpenTotalChartInModal(false)}
                    />
                  </DashboardWidget>
                </group>
              )} */}
              {!forceReload &&
                <DataTable
                  tableLayout={"auto"}
                  ellipsis={true}
                  columns={isCurrentDomainBased ? groupedColumns : columns}
                  bordered={true}
                  customPagination={false}
                  reload={reload}
                  dataPagination={true}
                  customPageSize={ 999999999 }
                  getPageAsync={getPage}
                  loading={loading || (radioMode !== "raw" && loadingAdminFilterSet)}
                  showTotal={true}
                  size="small"
                  rowKey={record => isCurrentDomainBased ? `${record.domain}` : `${record.queryTime}`}
                  totalRecords={totalRecords}
                  expandable={isCurrentDomainBased ? {
                    expandedRowRender: (record) => (
                      <group>
                        <view className="table_cont" data-background="none" data-min-height='30'>
                          {
                            radioMode === 'raw' &&
                            <group data-weight="600">
                              <text data-space="10">Query Time</text>
                              <separator data-horizontal="" data-margin="none"></separator>
                            </group>
                          }
                          <DataTable
                            showHeader={radioMode !== 'raw'}
                            tableLayout={"auto"}
                            ellipsis={true}
                            columns={radioMode === 'raw' ? expandedRawTableColumns : expandedTableColumns}
                            bordered={true}
                            dataSource={radioMode === 'raw' ? record.queryTimes : record.queryData}
                            size="small"
                            rootClassName={`no-margin-table ${radioMode !== 'raw' ? "no-margin-block-table" : ""}`}
                          />
                        </view>
                      </group>

                    ),
                  } : undefined}
                />
              }
            </view>
          </>
        )}
      </view>
      {
        showChartModal &&
        <CustomChartModal
          adaptive={true}
          options={{
            chart: {
              height: 200,
              type: 'pie',
              events: {
                dataPointSelection: (event: any, chartContext: any, config: any) => {
                  const selectedIndex = config.dataPointIndex;

                  const data = chartDatasource[selectedIndex];

                  let timeSlices = [];

                  const queryTimes = [...data.queryTimes];
                  const timeArray = queryTimes.reverse();

                  let timeSliceItem = null;
                  for (let index = 0; index < timeArray.length; index++) {
                    const element = timeArray[index];

                    if (!timeSliceItem) {
                      timeSliceItem = {
                        // start: moment(element).toISOString(),
                        start: moment(element).format("HH:mm"),
                        end: ""
                      }
                    }

                    if (timeSliceItem && moment(timeArray[index + 1]).diff(moment(element), "minutes") > TIME_SLICE_MINUTES) {
                      if (moment(element).format("HH:mm") === timeSliceItem.start) {
                        timeSliceItem.end = moment(element).add(TIME_SLICE_MINUTES - 1).format("HH:mm")
                      } else {
                        timeSliceItem.end = moment(element).format("HH:mm")
                      }
                      timeSlices.push(timeSliceItem);
                      timeSliceItem = null;
                    }

                    if (index === timeArray.length - 1 && timeSliceItem && !timeSliceItem.end) {
                      if (moment(element).format("HH:mm") === timeSliceItem.start) {
                        timeSliceItem.end = moment(element).add(TIME_SLICE_MINUTES - 1).format("HH:mm")
                      } else {
                        timeSliceItem.end = moment(element).format("HH:mm")
                      }
                      timeSlices.push(timeSliceItem);
                      timeSliceItem = null;
                    }

                    // if (timeSliceItem && moment(timeArray[index + 1]).diff(moment(element), "minutes") > TIME_SLICE_MINUTES) {
                    //   if (moment(element).toISOString() === timeSliceItem.start) {
                    //     timeSliceItem.end = moment(element).add(TIME_SLICE_MINUTES - 1).toISOString()
                    //   } else {
                    //     timeSliceItem.end = moment(element).toISOString()
                    //   }
                    //   timeSlices.push(timeSliceItem);
                    //   timeSliceItem = null;
                    // }

                    // if (index === timeArray.length - 1 && timeSliceItem && !timeSliceItem.end) {
                    //   if (moment(element).toISOString() === timeSliceItem.start) {
                    //     timeSliceItem.end = moment(element).add(TIME_SLICE_MINUTES - 1).toISOString()
                    //   } else {
                    //     timeSliceItem.end = moment(element).toISOString()
                    //   }
                    //   timeSlices.push(timeSliceItem);
                    //   timeSliceItem = null;
                    // }
                  }

                  setTimeChartData({...data, timeSlices });
                  setShowTimelineChartModal(true);
                  // You can add any additional actions here
                }
              }
            },
            plotOptions: {
              pie: {
                customScale: 0.8,
                size: 200
              }
            },
            grid: {
              row: {
                colors: ['#f3f3f3', 'transparent'], // takes an array which will be repeated on columns
                opacity: 0.5
              },
            },
            tooltip: {
              y: {
                formatter: function (val: any) {
                  return `${Number(val).toFixed(2)} MB`;
                }
              }
            },
            labels: chartDatasource.map(x => x.domain),
            legend: {
              position: 'bottom'
            }
          }}
          series={chartDatasource.map(x => x.percent)}
          type="pie"
          title="Top Sites"
          open={showChartModal}
          childrenComponent={
            !!showTimelineChartModal && !!timeChartData &&
            <>
              <separator data-horizontal=""></separator>
              <group data-length="auto" data-space="10" data-max-height='50'>
                <text data-weight="700">{timeChartData.domain}</text>
                <Button
                  material
                  icon="close"
                  data-position="right"
                  onClick={() => {setShowTimelineChartModal(false)}}
                />
              </group>
              <Timeline activities={ timeChartData.timeSlices.map((x: any) => {return { start: x.start, end:x.end}}) } />
              {/* [
                { start: '10:15', end: '12:45' },
                { start: '18:30', end: '19:15' },
              ] */}
            </>
          }
          height="70%"
          onClose={() => setShowChartModal(false)}/>
      }
      {/* {
        showTimelineChartModal && timeChartData &&
        <CustomChartModal
          adaptive={true}
          options={{
            chart: {
              height: 350,
              type: 'rangeBar'
            },
            plotOptions: {
              bar: {
                horizontal: true,
                isDumbbell: true,
                dumbbellColors: [['#EC7D31', '#36BDCB']]
              }
            },
            colors: ['#EC7D31', '#36BDCB'],
            fill: {
              type: 'gradient',
              gradient: {
                gradientToColors: ['#36BDCB'],
                inverseColors: false,
                stops: [0, 100]
              }
            },
            grid: {
              xaxis: {
                lines: {
                  show: true
                }
              },
              yaxis: {
                lines: {
                  show: false
                }
              }
            },
            xaxis: {
              type: 'datetime',
              tickAmount: 24, // This will divide the axis into 24 hourly ticks.
              labels: {
                format: 'HH:mm' // Formats the label to show hours and minutes
              },
              min: new Date(currentActivityDay + 'T00:00:00').getTime(), // Start time
              max: new Date(currentActivityDay + 'T23:59:59').getTime(), // End time for a day
            }
          }}
          series={[
            {
              data: timeChartData.timeSlices.map((item: any) => {
                return {
                  x: timeChartData.domain,
                  y:
                  [
                    new Date(item.start).getTime(),
                    new Date(item.end).getTime()
                  ]
                }
              })
              //  [
              //   {
              //     x: timeChartData.domain,
              //     // y: timeChartData.queryTimes.reverse().map((x: any) => new Date(x).getTime())
              //     // y:
              //     // [
              //     //   timeChartData.queryTimes.reverse().map((x: any) => new Date(x).getTime())[0],
              //     //   timeChartData.queryTimes.reverse().map((x: any) => new Date(x).getTime())[0]
              //     // ]
              //     y:
              //     [
              //       new Date("2024-11-12T02:34:34-07:00").getTime(),
              //       new Date("2024-11-12T03:34:34-07:00").getTime()
              //     ]
              //   },
              //   {
              //     x: timeChartData.domain,
              //     // y: timeChartData.queryTimes.reverse().map((x: any) => new Date(x).getTime())
              //     // y:
              //     // [
              //     //   timeChartData.queryTimes.reverse().map((x: any) => new Date(x).getTime())[0],
              //     //   timeChartData.queryTimes.reverse().map((x: any) => new Date(x).getTime())[0]
              //     // ]
              //     y:
              //     [
              //       new Date("2024-11-12T07:34:34-07:00").getTime(),
              //       new Date("2024-11-12T08:34:34-07:00").getTime(),
              //     ]
              //   }
              // ]
            }
          ]}
          type="rangeBar"
          title={`Timeline: ${timeChartData.domain}`}
          open={showTimelineChartModal}
          onClose={onTimeLineChartCloseClick}/>
      } */}
    </>
  );
}
